import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    BarChart2, TrendingUp, CheckCircle, AlertCircle,
    MessageSquare, Package, Layers, RefreshCw, Database,
    ShieldAlert, FileText, Globe, Zap,
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend,
} from 'recharts';
import { getDashboardStats, StatsResult } from '../services/api';
import toast from 'react-hot-toast';

const PIE_COLORS = ['#10b981', '#f59e0b', '#ef4444', '#6b7280'];

export default function DashboardPage() {
    const [stats, setStats] = useState<StatsResult | null>(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const data = await getDashboardStats();
            setStats(data);
        } catch {
            toast.error('数据加载失败，请稍后重试');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const fmt = (n: number) =>
        new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(n);

    if (loading) {
        return (
            <div className="p-6 max-w-6xl mx-auto animate-fade-in">
                <div className="mb-8 h-8 w-48 bg-white/[0.06] rounded-lg animate-pulse" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="card h-24 animate-pulse bg-white/[0.03]" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="card h-64 animate-pulse bg-white/[0.03]" />
                    ))}
                </div>
            </div>
        );
    }

    if (!stats) return null;

    // ── Empty state (new user: no classifications yet) ────────────────────────
    if (stats.total_classifications === 0) {
        return (
            <div className="p-6 max-w-4xl mx-auto animate-fade-in">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <BarChart2 size={20} className="text-brand-400" />
                            <h1 className="text-xl font-bold text-white">数据概览</h1>
                        </div>
                        <p className="text-slate-400 text-sm">欢迎使用税码通，开始你的第一次关税分析</p>
                    </div>
                </div>

                {/* Welcome banner */}
                <div className="card border border-brand-500/25 bg-brand-500/5 mb-6 text-center py-10">
                    <div className="text-4xl mb-3">🚀</div>
                    <h2 className="text-xl font-bold text-white mb-2">账户已就绪</h2>
                    <p className="text-slate-400 text-sm max-w-md mx-auto leading-relaxed">
                        上传产品 CSV 或输入产品描述，AI 将自动完成 HTS 编码分类、关税计算与多国货源成本对比。
                    </p>
                </div>

                {/* Quick start actions */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                        {
                            icon: ShieldAlert,
                            title: '关税风险扫描',
                            desc: '上传 CSV，一键扫描全部 SKU 的对华关税风险，自动标注高危品类',
                            href: '/scan',
                            color: 'text-red-400',
                            bg:   'bg-red-500/10',
                            cta:  '开始扫描',
                        },
                        {
                            icon: Globe,
                            title: '多国货源对比',
                            desc: '对比中国、越南、墨西哥、印度四国税率，找出含税成本最低的货源方案',
                            href: '/scan',
                            color: 'text-violet-400',
                            bg:   'bg-violet-500/10',
                            cta:  '查看对比',
                        },
                        {
                            icon: Zap,
                            title: '单品 HTS 分类',
                            desc: '输入产品描述，AI 秒级返回 HTS 编码、置信度与备选方案',
                            href: '/classify',
                            color: 'text-brand-400',
                            bg:   'bg-brand-500/10',
                            cta:  '立即分类',
                        },
                        {
                            icon: FileText,
                            title: '批量上传',
                            desc: '支持 1000 行 CSV 批量处理，进度实时更新，结果可导出 PDF/CSV',
                            href: '/batch',
                            color: 'text-emerald-400',
                            bg:   'bg-emerald-500/10',
                            cta:  '批量处理',
                        },
                    ].map((item) => (
                        <Link
                            key={item.title}
                            to={item.href}
                            className="card hover:border-white/15 hover:-translate-y-0.5 transition-all group cursor-pointer"
                        >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${item.bg}`}>
                                <item.icon size={20} className={item.color} />
                            </div>
                            <h3 className="text-sm font-semibold text-white mb-1">{item.title}</h3>
                            <p className="text-xs text-slate-500 leading-relaxed mb-3">{item.desc}</p>
                            <span className={`text-xs font-medium ${item.color} group-hover:underline`}>
                                {item.cta} →
                            </span>
                        </Link>
                    ))}
                </div>

                {/* Quick tips */}
                <div className="mt-6 card border border-amber-500/20 bg-amber-500/5">
                    <p className="text-xs font-semibold text-amber-400 mb-2">💡 快速入门提示</p>
                    <ul className="space-y-1.5 text-xs text-slate-400 leading-relaxed">
                        <li>• CSV 格式：<span className="font-mono text-slate-300">sku,description[,unit_cost]</span> — 含 unit_cost 列自动开启利润分析</li>
                        <li>• 多国货源对比在扫描结果中展开任意产品行即可查看</li>
                        <li>• 税率数据基于 2025–2026 年关税行政令，含 145% 对华综合税率</li>
                    </ul>
                </div>
            </div>
        );
    }

    const confDist = [
        { name: '高置信度 ≥80%', value: stats.confidence_distribution.high },
        { name: '中置信度 50–79%', value: stats.confidence_distribution.medium },
        { name: '低置信度 <50%', value: stats.confidence_distribution.low },
        { name: '失败', value: stats.confidence_distribution.failed },
    ].filter((d) => d.value > 0);

    const dailyData = stats.daily_activity.map((d) => ({
        day: new Date(d.day).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
        count: d.count,
    }));

    return (
        <div className="p-6 max-w-6xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <BarChart2 size={20} className="text-brand-400" />
                        <h1 className="text-xl font-bold text-white">数据概览</h1>
                    </div>
                    <p className="text-slate-400 text-sm">实时分类统计、AI 准确率与关税分析</p>
                </div>
                <button onClick={load} className="btn-secondary gap-2 text-sm">
                    <RefreshCw size={14} />
                    刷新
                </button>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                {[
                    {
                        icon: Package,
                        label: '总分类数',
                        value: fmt(stats.total_classifications),
                        sub: '累计产品',
                        color: 'text-brand-400',
                    },
                    {
                        icon: TrendingUp,
                        label: '平均置信度',
                        value: `${stats.avg_confidence}%`,
                        sub: 'AI 准确率',
                        color: stats.avg_confidence >= 75 ? 'text-emerald-400' : 'text-amber-400',
                    },
                    {
                        icon: Layers,
                        label: '批量任务',
                        value: fmt(stats.batch_stats.total_batches),
                        sub: `${fmt(stats.batch_stats.total_batch_items)} SKUs 处理`,
                        color: 'text-violet-400',
                    },
                    {
                        icon: MessageSquare,
                        label: '用户反馈',
                        value: fmt(stats.feedback_stats.total),
                        sub: `纠错率 ${stats.feedback_stats.correction_rate_pct}%`,
                        color: stats.feedback_stats.correction_rate_pct > 20 ? 'text-amber-400' : 'text-emerald-400',
                    },
                    {
                        icon: Database,
                        label: 'HTS 参考库',
                        value: fmt(stats.hts_codes_count),
                        sub: stats.hts_codes_count > 0 ? '数据库已就绪 ✓' : '⚠ 未初始化',
                        color: stats.hts_codes_count > 0 ? 'text-emerald-400' : 'text-red-400',
                    },
                ].map(({ icon: Icon, label, value, sub, color }) => (
                    <div key={label} className="card">
                        <div className="flex items-start justify-between mb-3">
                            <Icon size={16} className={color} />
                        </div>
                        <p className={`text-2xl font-bold ${color}`}>{value}</p>
                        <p className="text-xs font-medium text-white mt-0.5">{label}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{sub}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* 7-day activity bar chart */}
                <div className="card">
                    <p className="section-header mb-4">近 7 日分类量</p>
                    {dailyData.length === 0 ? (
                        <div className="flex items-center justify-center h-40 text-slate-600 text-sm">
                            暂无数据 — 开始分类后显示
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart data={dailyData} barSize={18}>
                                <XAxis
                                    dataKey="day"
                                    tick={{ fill: '#64748b', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={{ fill: '#64748b', fontSize: 11 }}
                                    axisLine={false}
                                    tickLine={false}
                                    allowDecimals={false}
                                />
                                <Tooltip
                                    contentStyle={{
                                        background: '#1d1c38',
                                        border: '1px solid rgba(99,102,241,0.2)',
                                        borderRadius: '10px',
                                        color: '#e2e8f0',
                                        fontSize: 12,
                                    }}
                                    cursor={{ fill: 'rgba(99,102,241,0.08)' }}
                                />
                                <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="分类数" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {/* Confidence distribution pie */}
                <div className="card">
                    <p className="section-header mb-4">置信度分布</p>
                    {confDist.length === 0 ? (
                        <div className="flex items-center justify-center h-40 text-slate-600 text-sm">
                            暂无数据
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height={180}>
                            <PieChart>
                                <Pie
                                    data={confDist}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={72}
                                    paddingAngle={3}
                                    dataKey="value"
                                >
                                    {confDist.map((_, i) => (
                                        <Cell key={i} fill={PIE_COLORS[i]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{
                                        background: '#1d1c38',
                                        border: '1px solid rgba(99,102,241,0.2)',
                                        borderRadius: '10px',
                                        color: '#e2e8f0',
                                        fontSize: 12,
                                    }}
                                />
                                <Legend
                                    iconType="circle"
                                    iconSize={9}
                                    formatter={(v) => <span className="text-xs text-slate-400">{v}</span>}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top HTS codes table */}
                <div className="card p-0 overflow-hidden">
                    <div className="px-4 py-3 border-b border-white/[0.06]">
                        <p className="text-sm font-semibold text-white">Top 10 HTS 编码</p>
                        <p className="text-xs text-slate-500 mt-0.5">按分类频次排序</p>
                    </div>
                    {stats.top_hts_codes.length === 0 ? (
                        <div className="p-8 text-center text-slate-600 text-sm">暂无分类记录</div>
                    ) : (
                        <div className="divide-y divide-white/[0.04]">
                            {stats.top_hts_codes.map((item, i) => (
                                <div key={item.hts_code} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/[0.02] transition-colors">
                                    <span className="text-xs text-slate-600 w-5 text-right flex-shrink-0">
                                        {i + 1}
                                    </span>
                                    <span className="hts-code flex-shrink-0">{item.hts_code_formatted}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="h-1.5 bg-surface-2 rounded-full">
                                            <div
                                                className="h-full bg-brand-500 rounded-full"
                                                style={{
                                                    width: `${Math.min(
                                                        100,
                                                        (item.count / (stats.top_hts_codes[0]?.count || 1)) * 100,
                                                    )}%`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <span className="text-xs text-slate-400 flex-shrink-0 w-8 text-right">
                                        {item.count}
                                    </span>
                                    <span className={`text-xs flex-shrink-0 w-10 text-right ${
                                        item.avg_confidence >= 80
                                            ? 'text-emerald-400'
                                            : item.avg_confidence >= 50
                                            ? 'text-amber-400'
                                            : 'text-red-400'
                                    }`}>
                                        {item.avg_confidence}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Model & system info */}
                <div className="space-y-4">
                    <div className="card space-y-3">
                        <p className="section-header">AI 模型信息</p>
                        {[
                            {
                                label: 'AI 模型',
                                value: stats.ai_provider.model,
                                color: 'text-brand-400',
                            },
                            {
                                label: 'AI 服务商',
                                value: stats.ai_provider.provider === 'qwen' ? '通义千问 (Qwen)' : 'DeepSeek',
                                color: 'text-white',
                            },
                            { label: '批量并发', value: '5 并行请求', color: 'text-emerald-400' },
                            { label: '结构化输出', value: 'Function Calling (类型安全)', color: 'text-white' },
                            { label: '关税数据', value: '2025 EO 版本 (145% CN)', color: 'text-amber-400' },
                        ].map(({ label, value, color }) => (
                            <div key={label} className="flex justify-between text-sm">
                                <span className="text-slate-500">{label}</span>
                                <span className={`font-medium ${color}`}>{value}</span>
                            </div>
                        ))}
                    </div>

                    <div className="card space-y-2">
                        <div className="flex items-center gap-2">
                            <AlertCircle size={13} className="text-amber-400 flex-shrink-0" />
                            <p className="text-xs font-semibold text-amber-300">关税免责声明</p>
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed">
                            关税税率依据 2025 年 4 月行政令（145% 对华关税、取消 De Minimis）。
                            关税政策随时可能通过行政令变更。所有分类结果仅供参考，
                            正式申报前请咨询持牌报关行。
                        </p>
                    </div>

                    <div className="card">
                        <p className="section-header">反馈质量</p>
                        <div className="flex items-end gap-3">
                            <div>
                                <p className="text-3xl font-bold text-white">
                                    {stats.feedback_stats.corrections}
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">用户纠错次数</p>
                            </div>
                            <div className="flex-1 pb-1">
                                <div className="flex items-center gap-2 mb-1">
                                    {stats.feedback_stats.correction_rate_pct <= 15 ? (
                                        <CheckCircle size={13} className="text-emerald-400" />
                                    ) : (
                                        <AlertCircle size={13} className="text-amber-400" />
                                    )}
                                    <span className={`text-sm font-semibold ${
                                        stats.feedback_stats.correction_rate_pct <= 15
                                            ? 'text-emerald-400'
                                            : 'text-amber-400'
                                    }`}>
                                        {stats.feedback_stats.correction_rate_pct}% 纠错率
                                    </span>
                                </div>
                                <p className="text-xs text-slate-600">
                                    目标值 &lt;15% — 纠错数据将用于模型微调
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
